import {
  Button as MuiButton,
  ButtonProps as MUIButtonProps,
} from "@mui/material";
import classNames from "classnames";
import { forwardRef, ReactNode, ReactText, useRef } from "react";
import { toast } from "react-toastify";
import Link, { LinkProps } from "../../../elements/Link/Link";
import { SoundWaveLoader } from "../../../elements/SoundWaveLoader/SoundWaveLoader";
import "./Button.css";
import {
  buttonStyle,
  buttonStyleDisabled,
  ButtonVariant,
  muiColor,
  muiVariant,
} from "./utils";

export interface ButtonProps extends Omit<MUIButtonProps, "variant"> {
  children?: ReactNode;
  disableText?: string;
  fullWidth?: boolean;
  labelIcon?: ReactNode;
  LinkProps?: Omit<LinkProps, "to">;
  loading?: boolean;
  search?: string;
  variant?: ButtonVariant;
}

/**
 * Button using MUI Button component.
 * The styles and variants should mimic the old button.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      children,
      className,
      disabled,
      disableText,
      fullWidth,
      href,
      search,
      labelIcon,
      LinkProps,
      loading,
      style,
      sx,
      variant = ButtonVariant.PRIMARY,
      ...restProps
    } = props;
    const toastId = useRef<ReactText>("");
    const showDisableToast = () => {
      if (!disabled || !disableText) return;
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error(disableText);
      }
    };

    const finalButtonStyle = disabled
      ? buttonStyleDisabled[variant]
      : buttonStyle[variant];

    const buttonClass = classNames(finalButtonStyle, className);

    const buttonContent = loading ? (
      <SoundWaveLoader
        whiteLoader={variant === ButtonVariant.PRIMARY}
        width={50}
        height={12}
      />
    ) : (
      children
    );

    const button = (
      <MuiButton
        className={buttonClass}
        color={muiColor[variant]}
        disabled={disabled}
        endIcon={!loading && labelIcon}
        fullWidth={fullWidth}
        onMouseOver={showDisableToast}
        ref={ref}
        variant={muiVariant[variant]}
        style={{
          // fullWidth prop isn't working properly, so overriding the style here
          ...(fullWidth && { width: "100%" }),
          ...style,
        }}
        sx={sx}
        {...restProps}
        // Must be placed after the spread, otherwise it will get overridden
        // We couldn't de-structure onClick either because TS 4.5.5 wasn't able to infer the type correctly
        onClick={props.onClick}
      >
        {buttonContent}
      </MuiButton>
    );

    const url = `${href}${search ? `?${search}` : ""}`;
    return href === undefined ? (
      button
    ) : (
      <Link
        className={LinkProps?.className}
        to={!disabled ? url : "#"}
        onClick={LinkProps?.onClick}
        onMouseOver={showDisableToast}
        target={href.match(/^http/) ? "_blank" : undefined}
        rel={href.match(/^http/) ? "noreferrer" : undefined}
        style={{
          ...(fullWidth && { width: "100%" }),
          ...LinkProps?.style,
        }}
        {...LinkProps}
      >
        {button}
      </Link>
    );
  },
);

Button.displayName = "Button";
